/*
 * Role model + affordance gating.
 *
 * Three roles (viewer < operator < admin), ranked. `can(role, capability)`
 * is the single predicate the UI uses to show/enable affordances; the backend
 * still enforces (`require_role`), this only avoids dangling controls a user
 * can't use. Capabilities are coarse, matching the launch console surface.
 */

import type { Role } from "../api/client";

export const ROLE_RANK: Record<Role, number> = {
  viewer: 0,
  operator: 1,
  admin: 2,
};

export type Capability =
  | "view" // see runs, reports, product/credential state (viewer+)
  | "operate" // launch runs, pause/resume/cancel, recovery actions (operator+)
  | "configure" // edit products, set/rotate/delete credentials (operator+)
  | "administer"; // invite members, manage brand (admin)

const CAPABILITY_MIN_ROLE: Record<Capability, Role> = {
  view: "viewer",
  operate: "operator",
  configure: "operator",
  administer: "admin",
};

/** Does `role` (if any) meet the minimum rank for `capability`? */
export function can(role: Role | null, capability: Capability): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[CAPABILITY_MIN_ROLE[capability]];
}

/** Does `role` meet-or-exceed `min`? (Direct rank comparison.) */
export function atLeast(role: Role | null, min: Role): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[min];
}
